import { observer } from 'mobx-react-lite';
import { InputFieldLabel } from '../../../../lib/components/atomic/input/shared/fieldLabel';
import {
  FormHelperText,
  Typography,
  Box,
  MenuItem,
  IconButton,
  FormControl,
  Select as MuiSelect,
  Input as MuiInput,
  ThemeProvider,
  Tooltip
} from '@mui/material';
import { descriptionTheme } from '../../createCustomBenefit/steps/benefitProfile';
import { useTranslation } from 'react-i18next';
import { useCreateBonus } from '../hooks/useCreateBonus';
import { useStepper } from '../../partials/actionStepperPartial';
import { ArrowForwardIconSvg, CloseIconSvg } from '../../../../lib/icons';
import ArrowDown from '../../../../lib/icons/svg/arrow_down.svg?react';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  PlaceholderEditor,
  PlaceholderEditorHandle,
  KNOWN_PLACEHOLDERS
} from '../components/PlaceholderEditor';
import { Drawer } from '../../../../lib/components/atomic/dataDisplay/drawer';
import { InfoSection } from '../../../../lib/components/atomic/typography/InfoSection';
import {
  Chip,
  FormSelect,
  FormTextField,
  ConfirmationDialog,
  InformationDialog,
  Input
} from '../../../../lib/components';
import { Button } from '../../../../lib/components/atomic/button';
import { useListBonusMessageTemplates } from '../../../../queries/bonus';
import { useStore } from '../../../../hooks/useStore';
import { FormikValues, useFormikContext, useFormik } from 'formik';
import { useListEmployees } from '../../../../queries/employee';
import { FormDialog } from '../../../../lib/components/compound/dialogs/formDialog';
import { BonusMessageTemplate, EmployeeListItem } from '@pxtra-client-lib-legacy/gen';
import {
  useCreateBonusMessageTemplate,
  useDeleteBonusMessageTemplate
} from '../../../../queries/bonus/mutations';
import { enqueueSnackbar } from 'notistack';
import * as Yup from 'yup';
import { useActions } from '../../../../hooks/useActions';
import { HintBox } from '../../../../lib/components/atomic/typography/hintBox';
import { messageSchema } from '../utils/validationSchemas';

const DEFAULT_PLACEHOLDERS = KNOWN_PLACEHOLDERS.map((name) => `{{${name}}}`);

const fullYearsBetween = (startIso?: string | null, endDate: Date = new Date()): number | null => {
  if (!startIso) return null;
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return null;
  let years = endDate.getFullYear() - start.getFullYear();
  const beforeAnniversary =
    endDate.getMonth() < start.getMonth() ||
    (endDate.getMonth() === start.getMonth() && endDate.getDate() < start.getDate());
  if (beforeAnniversary) years -= 1;
  return years;
};

const PreviewMessageField = observer(
  ({
    employees,
    originalMessage
  }: {
    employees: Array<{ value: string; text: string; employee: EmployeeListItem }>;
    originalMessage: string;
  }) => {
    const { t } = useTranslation();
    const { values } = useFormikContext<{ employees: string }>();
    const { companyStore } = useStore();

    const previewMessage = useMemo(() => {
      const selectedEmployee = employees.find((emp) => emp.value === values.employees)?.employee;
      if (!selectedEmployee && !companyStore.company?.name) return originalMessage;

      const today = new Date();
      const replacements: Record<string, string> = {
        '{{first_name}}': selectedEmployee?.first_name ?? '',
        '{{last_name}}': selectedEmployee?.last_name ?? '',
        '{{company}}': companyStore.company?.name ?? '',
        '{{age}}': (() => {
          const age = fullYearsBetween(selectedEmployee?.birth_date, today);
          return age === null ? '' : String(age);
        })(),
        '{{years_of_service}}': (() => {
          const tenure = fullYearsBetween(selectedEmployee?.entry_date, today);
          return tenure === null ? '' : String(tenure);
        })()
      };

      let result = originalMessage;
      Object.entries(replacements).forEach(([token, value]) => {
        result = result.replaceAll(token, value);
      });
      return result;
    }, [originalMessage, employees, values.employees, companyStore.company?.name]);

    return (
      <div className="w-full ml-4">
        <InputFieldLabel label={t('bonus:previewMessage')} />
        <ThemeProvider theme={descriptionTheme}>
          <MuiInput
            name="preview"
            multiline={true}
            minRows={8}
            value={previewMessage}
            disableUnderline
            inputProps={{ readOnly: true }}
            className="rounded-lg p-3"
          />
        </ThemeProvider>
      </div>
    );
  }
);

export const Message = observer(() => {
  const { t } = useTranslation();
  const { newBonus } = useCreateBonus();
  const { companyStore } = useStore();
  const { selectedEmployees } = useActions();

  const {
    setNextButtonConfig,
    activeStep,
    setActiveStep,
    showInfoDrawer,
    setShowInfoDrawer,
    editMode
  } = useStepper();

  const { values, errors, touched, handleBlur, setFieldValue, submitForm } = useFormik({
    initialValues: {
      message: newBonus.message,
      selectedTemplateId: newBonus.selectedTemplateId
    },
    enableReinitialize: true,
    validationSchema: messageSchema(t),
    onSubmit: (values) => {
      newBonus.message = values.message;
      newBonus.selectedTemplateId = values.selectedTemplateId;
      editMode ? setActiveStep(6) : setActiveStep(activeStep + 1);
    }
  });

  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [showExistingTemplateDialog, setShowExistingTemplateDialog] = useState(false);
  const [templateOriginalMessage, setTemplateOriginalMessage] = useState<string | null>(null);
  const [existingTemplate, setExistingTemplate] = useState<BonusMessageTemplate>();

  const editorRef = useRef<PlaceholderEditorHandle>(null);
  const DESCRIPTION_MAX_LENGTH = 800;

  const { data: bonusMessageTemplates } = useListBonusMessageTemplates(
    companyStore.company?.id ?? ''
  );

  const { data: employeesData = { pages: [], pageParams: [] } } = useListEmployees(
    companyStore.company?.id,
    {
      enabled: !!companyStore.company?.id
    }
  );

  const flattenedData = useMemo(() => employeesData.pages.flat(), [employeesData]);

  const employees = useMemo(() => {
    return flattenedData
      .filter((emp) => emp.id && selectedEmployees[emp.id])
      .map((i) => ({
        text: `${i.first_name ?? ''} ${i.last_name ?? ''}`.trim(),
        value: i.id ?? '',
        employee: i
      }));
  }, [flattenedData, selectedEmployees]);

  const { mutateAsync: createBonusMessageTemplate } = useCreateBonusMessageTemplate();
  const { mutateAsync: deleteBonusMessageTemplate } = useDeleteBonusMessageTemplate();
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);

  const placeholders = DEFAULT_PLACEHOLDERS;

  const placeholdersInText = useMemo(() => {
    const message = values.message ?? '';
    const present = new Set<string>();
    placeholders.forEach((placeholder) => {
      if (message.includes(placeholder)) present.add(placeholder);
    });
    return present;
  }, [placeholders, values.message]);

  const isPlaceholderInText = (placeholder: string): boolean => placeholdersInText.has(placeholder);

  const tooltipFor = useCallback(
    (placeholder: string): string | undefined => {
      const name = placeholder.replace('{{', '').replace('}}', '');
      if (name === 'company') {
        return companyStore.company?.name ?? undefined;
      }
      const key = `bonus:placeholderTooltips->${name}`;
      const tooltip = t(key as never);
      return tooltip && tooltip !== key ? tooltip : undefined;
    },
    [companyStore.company?.name, t]
  );

  const labelFor = useCallback(
    (placeholder: string): string => {
      const name = placeholder.replace('{{', '').replace('}}', '');
      const key = `bonus:placeholderLabels->${name}`;
      const label = t(key as never);
      return label && label !== key ? label : name;
    },
    [t]
  );

  const insertPlaceholder = (placeholder: string) => {
    if (editorRef.current) {
      editorRef.current.insertPlaceholder(placeholder);
    } else {
      setFieldValue('message', (values.message ?? '') + placeholder);
    }
  };

  const bonusMessageTemplatesOptions = useMemo(() => {
    if (!bonusMessageTemplates?.results) return [];

    return bonusMessageTemplates.results.map((item) => ({
      value: item.id,
      text: item.name,
      company: item.company
    }));
  }, [bonusMessageTemplates]);

  const handleTemplateChange = (templateId: string) => {
    setFieldValue('selectedTemplateId', templateId);

    const selectedTemplate = bonusMessageTemplates?.results.find((item) => item.id === templateId);

    if (selectedTemplate) {
      setFieldValue('message', selectedTemplate.message);
      setTemplateOriginalMessage(selectedTemplate.message);

      newBonus.messageTemplate = {
        name: selectedTemplate.name,
        message: selectedTemplate.message
      };
    }
  };

  const isMessageEdited = useMemo(() => {
    if (!values.message) return false;
    if (templateOriginalMessage === null) return true;
    return values.message !== templateOriginalMessage;
  }, [values.message, templateOriginalMessage]);

  const handleCreateTemplate = async (formikValues: FormikValues) => {
    try {
      const templateExists = bonusMessageTemplates?.results.find(
        (template) => template.name.toLowerCase() === formikValues.name.toLowerCase()
      );

      if (templateExists) {
        setExistingTemplate(templateExists);
        setShowExistingTemplateDialog(true);
        return;
      }

      const newTemplate = await createBonusMessageTemplate({
        template: {
          name: formikValues.name,
          message: values.message ?? '',
          company: companyStore.company?.id ?? ''
        }
      });

      if (newTemplate) {
        setFieldValue('selectedTemplateId', newTemplate.id);
        setTemplateOriginalMessage(newTemplate.message);
      }

      enqueueSnackbar(
        t('bonus:templatesSaved', {
          name: formikValues.name
        }),
        {
          variant: 'success'
        }
      );
      setShowSaveTemplateDialog(false);
    } catch {
      enqueueSnackbar(t('bonus:couldNotBeCreated'), { variant: 'error' });
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    try {
      await deleteBonusMessageTemplate({ id: templateToDelete });
      if (values.selectedTemplateId === templateToDelete) {
        setFieldValue('selectedTemplateId', '');
        setTemplateOriginalMessage(null);
      }
      enqueueSnackbar(t('bonus:templateDeleted'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('bonus:couldNotBeDeleted'), { variant: 'error' });
    }
    setTemplateToDelete(null);
  };

  const nextButtonConfig = useMemo(
    () => ({
      text: editMode ? t('common:CONFIRM') : t('common:NEXT'),
      endIcon: <ArrowForwardIconSvg />,
      onClick: () => {
        submitForm();
      }
    }),
    [editMode, t, submitForm]
  );

  useEffect(() => {
    setNextButtonConfig(nextButtonConfig);
  }, [nextButtonConfig, setNextButtonConfig]);

  return (
    <>
      <div className="flex flex-col">
        <div className="flex flex-col gap-y-5">
          <FormControl
            fullWidth
            sx={{
              '&:hover svg': {
                color: 'grey.900'
              }
            }}
          >
            <InputFieldLabel label={t('bonus:chooseTemplate') + ' ' + t('common:OPTIONAL')} />
            <MuiSelect
              name="benefit_set"
              value={values.selectedTemplateId ?? ''}
              onChange={(e) => handleTemplateChange(e.target.value as string)}
              displayEmpty
              input={<Input />}
              IconComponent={(iconProps) => (
                <ArrowDown
                  {...iconProps}
                  className={`${iconProps.className} cursor-pointer mr-1.5 h-6 w-6 top-3 text-primary-main`}
                />
              )}
              sx={{ height: '3rem' }}
              MenuProps={{
                PaperProps: {
                  className: 'max-w-[300px] max-h-[400px] overflow-y-auto rounded-lg shadow-500'
                },
                BackdropProps: {
                  invisible: true,
                  style: { backgroundColor: 'transparent', opacity: '0' }
                },
                MenuListProps: { sx: { py: 0 } }
              }}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography className="text-grey-500">
                      {t('bonus:placeholderTemplate')}
                    </Typography>
                  );
                }
                return (
                  bonusMessageTemplatesOptions.find((opt) => opt.value === selected)?.text ??
                  String(selected)
                );
              }}
            >
              {bonusMessageTemplatesOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{ height: '48px', display: 'flex', justifyContent: 'space-between' }}
                >
                  <span className="text-base">{option.text}</span>
                  {option.company && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTemplateToDelete(option.value as string);
                      }}
                      sx={{ ml: 1 }}
                    >
                      <CloseIconSvg className="w-5 h-5" />
                    </IconButton>
                  )}
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <div className="flex flex-row gap-2 items-center mb-4">
            <Typography className="text-grey-600 text-xs">
              {t('bonus:insertPlaceholder')}
            </Typography>
            <div className="flex flex-wrap gap-2">
              {placeholders.map((placeholder) => {
                const isActive = isPlaceholderInText(placeholder);
                const tooltipText = tooltipFor(placeholder) ?? '';
                return (
                  <Tooltip key={placeholder} title={tooltipText} arrow>
                    <span className="inline-block">
                      <Chip
                        label={labelFor(placeholder)}
                        onClick={() => insertPlaceholder(placeholder)}
                        color={isActive ? 'heaven' : 'default'}
                        variant={isActive ? 'filled' : 'outlined'}
                      />
                    </span>
                  </Tooltip>
                );
              })}
            </div>
          </div>

          <div>
            <InputFieldLabel label={t('bonus:message')} required />
            <PlaceholderEditor
              ref={editorRef}
              value={values.message ?? ''}
              onChange={(v) => setFieldValue('message', v)}
              onBlur={() => handleBlur({ target: { name: 'message' } })}
              placeholder={t('bonus:placeholderMessage')}
              hasError={Boolean(touched.message && errors.message)}
              labelFor={labelFor}
              ariaLabel={t('bonus:message')}
            />
            <FormHelperText
              sx={(theme) => ({
                marginLeft: '12px',
                color:
                  touched.message && errors.message
                    ? theme.palette.error.main
                    : theme.palette.grey[600]
              })}
            >
              {(touched.message && errors.message) ??
                `${values.message?.length ?? 0} / ${DESCRIPTION_MAX_LENGTH}`}
            </FormHelperText>
          </div>
          <div className="flex flex-row justify-end gap-x-2">
            <Button
              variant="primary"
              onClick={() => setShowPreviewDialog(true)}
              disabled={!values.message}
            >
              {t('common:PREVIEW')}
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowSaveTemplateDialog(true)}
              disabled={!values.message || !isMessageEdited}
            >
              {t('bonus:saveTemplate')}
            </Button>
          </div>
        </div>
      </div>

      <Drawer
        label={t('bonus:messageDrawer:title')}
        open={showInfoDrawer}
        closeDrawer={() => setShowInfoDrawer(false)}
      >
        <div className="flex flex-col gap-y-6">
          <InfoSection content={t('bonus:messageDrawer:texts', { returnObjects: true })} />
          <HintBox description={t('bonus:messageDrawer:hint', { returnObjects: true })} />
        </div>
      </Drawer>

      <FormDialog
        open={showPreviewDialog}
        onClose={() => setShowPreviewDialog(false)}
        title={t('common:PREVIEW')}
        onSubmit={() => setShowPreviewDialog(false)}
        saveButtonText={t('common:CLOSE')}
      >
        <>
          <FormSelect
            label={t('employee:EMPLOYEE_CHOOSE')}
            placeholder={t('common:missingEmployee')}
            options={employees}
            name="employees"
            className="text-left"
          />

          <PreviewMessageField employees={employees} originalMessage={values.message ?? ''} />
        </>
      </FormDialog>

      <FormDialog
        open={showSaveTemplateDialog}
        onClose={() => setShowSaveTemplateDialog(false)}
        title={t('bonus:saveTemplate')}
        saveButtonText={t('common:SAVE')}
        onSubmit={(values) => handleCreateTemplate(values)}
        validationSchema={{
          name: Yup.string().required(t('form:FIELD_IS_REQUIRED'))
        }}
      >
        <Box className="flex flex-col gap-y-3 bg-grey-50 p-4 pb-6 m-3 mr-0 w-full rounded-lg">
          <FormTextField
            label={t('bonus:templateName')}
            name="name"
            required
            className="bg-white"
            placeholder={t('bonus:placeholderName')}
          />
        </Box>
      </FormDialog>

      <InformationDialog
        open={showExistingTemplateDialog}
        onClose={() => setShowExistingTemplateDialog(false)}
        title={t('bonus:existingTemplate')}
        content={t('bonus:existingTemplateHelperText', { name: existingTemplate?.name })}
        onConfirm={() => setShowExistingTemplateDialog(false)}
        confirmButtonText={t('common:OK')}
      />

      <ConfirmationDialog
        open={!!templateToDelete}
        variant="delete"
        title={t('bonus:deleteTemplateTitle')}
        contentText={t('bonus:deleteTemplateContent')}
        onConfirm={handleDeleteTemplate}
        onClose={() => setTemplateToDelete(null)}
      />
    </>
  );
});
